import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useLightbox } from "../context/LightboxContext";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";

export default function UbuntuTweaks() {
    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "UBUNTU POWER-USER WIKI: FROM TERMINAL TO DESKTOP",
        "FEBRUARY 14, 2026",
        "8min. read",
    ];
    const showLightbox = useLightbox();

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p>
                Ubuntu is a robust platform, but for developers and power users, the "out-of-the-box" experience is just the starting line. This wiki covers the essential tweaks and scripts I use to transform a fresh install into a high-performance development machine.
            </p>

            <h2 className="post-sect">TERMINAL: TAB RENAMING</h2>
            <p>
                If you manage multiple projects or SSH sessions, your terminal tabs can quickly become a blur of "user@hostname". I use a simple shell function to rename tabs on the fly.
            </p>
            <p>
                Add this to your <code>~/.bashrc</code> or <code>~/.zshrc</code>:
            </p>
            <pre><code className="language-bash">{`# Function to rename terminal tab
function title() {
  echo -ne "\\033]0;$1\\007"
}
`}</code></pre>
            <p>
                Now, you can simply type <code>title "Production DB"</code> and your terminal tab will update instantly.
            </p>

            <h2 className="post-sect">GNOME CUSTOMIZATION</h2>
            <p>
                GNOME is beautiful but needs the right tools to unlock its full potential. The two absolute essentials are:
            </p>
            <ul className="lead">
                <li><em>GNOME Tweaks:</em> For managing fonts, themes, and basic behavior. <code>sudo apt install gnome-tweaks</code></li>
                <li><em>Extension Manager:</em> A better way to search and install shell extensions without using the browser plugin. <code>sudo apt install gnome-shell-extension-manager</code></li>
            </ul>

            <h2 className="post-sect">ESSENTIAL EXTENSIONS</h2>
            <p>
                Once you have the Extension Manager, I highly recommend these:
            </p>
            <ul className="lead">
                <li><em>Clipboard Indicator:</em> Keeps a history of everything you copy.</li>
                <li><em>GSConnect:</em> The GNOME implementation of KDE Connect. Seamlessly syncs your Android notifications and files with your desktop.</li>
                <li><em>Dash to Dock:</em> Turns the side bar into a customizable dock.</li>
            </ul>

            <h2 className="post-sect">KDE PLASMA: MAC-STYLE PANEL</h2>
            <p>
                For those who want a more "macOS" aesthetic with the power of Linux, KDE Plasma is the ultimate playground. While GNOME is streamlined, KDE allows for granular control over every pixel.
            </p>
            <p>
                I prefer this layout for its <em>minimalistic elegance</em>. Having the application menus detached from the window itself keeps the workspace uncluttered, which is especially beneficial on laptop screens where vertical space is a premium. From a UX standpoint, this leverages <em>Fitts's Law</em>: by placing menus at the screen's edge, they essentially have infinite height, making them much faster and easier to "hit" with the cursor. This setup also highlights the importance of <em>Software Craftsmanship</em>—when we put in the work to create a beautiful icon for our app, we want it prominently displayed in the dock or Plank where we can be proud of it. Plus, those bouncy, high-fidelity icons are just great to look at.
            </p>

            <h3 className="post-sect-sub">PLANK DOCK</h3>
            <p>
                To get that iconic dock, I use <em>Plank</em>. It's lightweight and fits perfectly with any desktop environment.
            </p>
            <pre><code className="language-bash">{`sudo apt install plank`}</code></pre>
            <p>
                Customizing Plank is easy—just hold <code>Ctrl</code> and right-click anywhere on the dock, or run:
            </p>
            <pre><code className="language-bash">{`plank --preferences`}</code></pre>
            <p>
                <em>Pro Tip:</em> Adjust the Icon Size to <code>48</code> or <code>64</code> and use the "Matte" theme for a premium feel.
            </p>

            <div className="post-img-container">
                <img
                    src="/images/shots/kde-macos-sample.png"
                    alt="KDE Plasma with macOS style panel"
                    className="img-fluid reveal rounded-lg shadow-lg mb-4"
                    onClick={() => showLightbox("/images/shots/kde-macos-sample.png")}
                    style={{ cursor: "zoom-in" }}
                />
                <p className="text-center italic small">Sample of a tweaked KDE Panel & Plank Dock</p>
            </div>

            <h3 className="post-sect-sub">PANEL LAYOUT: THE MAC RECIPE</h3>
            <p>
                To replicate the clean, functional look of the panel in the screenshot, you'll need to arrange specific KDE widgets in a precise order. Here is the layout "recipe":
            </p>
            <pre><code className="language-javascript">{`// Application Menu: (The Apple Logo) Place this on the far left. Use the Application Launcher widget and set the icon to an Apple logo.
// Window List: This widget displays the name of the active window. This is where you apply the QML fix below to keep it text-only and elegant.
// Global Menu: Place this immediately after the Window List. It displays the menus (File, Edit, etc.) of the currently active window.
// Spacer (Flexible): Add a flexible spacer here to push the remaining widgets to the right.
// System Tray: This contains your status icons (Network, Volume, Battery, etc.).
// Digital Clock: Displays the time, following the system tray.
// Search: The final widget on the far right for quick access to files and apps.`}</code></pre>

            <h3 className="post-sect-sub">WINDOW LIST QML FIX</h3>
            <p>
                One common issue in KDE when mimicking macOS is getting the Window List to show only titles (or specific behavior) without the default icons overlapping. This QML override in <code>main.qml</code> (usually found in the Plasma Window List widget source) fixes the content layout:
            </p>

            <pre><code className="language-javascript">{`// THIS IS THE FIX: We define a custom content item that ONLY shows text
contentItem: PC3.Label {
    text: if (tasksModel.activeTask.valid) {
        return tasksModel.data(tasksModel.activeTask, TaskManager.AbstractTasksModel.AppName) ||
               tasksModel.data(tasksModel.activeTask, 0)
    } else return i18n("Plasma Desktop")

    // font: parent.font
    // Use pixelSize for more stability in Plasma panels
    font {
        pixelSize: 14  // Adjust this number (14, 16, 18) to change size
        family: parent.font.family
    }

    elide: Text.ElideRight
    horizontalAlignment: Text.AlignLeft
    verticalAlignment: Text.AlignVCenter
    leftPadding: 5 // Adds a tiny bit of space from the edge
}

// Kill the original icon completely
iconSource: ""`}</code></pre>

            <h2 className="post-sect">DEVELOPER OPTIMIZATIONS</h2>
            <p>
                If you work with React, Expo, or large file systems, you'll eventually run into the <code>ENOSPC</code> error. This happens when the system runs out of file watchers. Fix it by increasing the inotify limit:
            </p>
            <pre><code className="language-bash">{`echo fs.inotify.max_user_watches=524288 | sudo tee -a /etc/sysctl.conf && sudo sysctl -p`}</code></pre>

            <p>
                These tweaks might seem small individually, but together they create a frictionless environment that lets you focus on what matters: <em>Building.</em>
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}
